<template>
  <div class="page-container stats-dashboard-page">
    <!-- 顶部导航栏 -->
    <div class="page-header">
      <el-button
        type="text"
        icon="el-icon-arrow-left"
        @click="$router.push({ name: 'deviceList' })"
      >
        返回列表
      </el-button>
      <span class="page-title">设备台账统计视图</span>
    </div>

    <!-- 汇总指标卡 -->
    <div
      v-loading="loading"
      class="stats-row"
    >
      <div class="stat-item primary">
        <div class="stat-item-value">
          {{ grandTotal }}
        </div>
        <div class="stat-item-label">
          设备总数
        </div>
      </div>
      <div
        v-for="(name, idx) in areaNames"
        :key="name"
        class="stat-item"
        :class="areaColorClass(idx)"
      >
        <div class="stat-item-value">
          {{ areaTotals[idx] || 0 }}
        </div>
        <div class="stat-item-label">
          {{ name }}
        </div>
      </div>
    </div>

    <!-- 系统分布图 + 数据矩阵 -->
    <div class="content-row">
      <!-- ECharts 堆叠横向柱状图 -->
      <div
        v-loading="loading"
        class="card chart-card"
      >
        <div class="card-header">
          <span class="card-title">消防系统设备数量情况</span>
          <span class="card-subtitle">点击柱体或系统名称查看设施类型明细</span>
        </div>
        <div
          v-if="!loading && !hasDeviceData"
          class="panel-empty"
        >
          暂无设备
        </div>
        <div
          v-show="loading || hasDeviceData"
          ref="chartContainer"
          class="chart-container"
        />
      </div>

      <!-- 系统 × 区域矩阵表 -->
      <div
        v-loading="loading"
        class="card table-card"
      >
        <div class="card-header card-header-with-action">
          <span class="card-title">设备数量统计矩阵</span>
          <el-button
            type="primary"
            plain
            icon="el-icon-download"
            :loading="matrixExportLoading"
            :disabled="!hasDeviceData"
            @click="handleExportMatrix"
          >
            导出
          </el-button>
        </div>
        <div
          v-if="!loading && !hasDeviceData"
          class="panel-empty matrix-empty"
        >
          暂无设备
        </div>
        <div
          v-show="loading || hasDeviceData"
          class="matrix-table-scroll"
        >
          <el-table
            :data="systems"
            border
            size="small"
            class="matrix-table"
            show-summary
            :summary-method="getSummaries"
          >
            <el-table-column
              label="消防系统"
              prop="systemName"
              min-width="130"
            >
              <template #default="{ row }">
                <el-button
                  type="text"
                  @click="openDrillDown(row)"
                >
                  {{ row.systemName }}
                </el-button>
              </template>
            </el-table-column>
            <el-table-column
              v-for="(name, idx) in areaNames"
              :key="name"
              :label="name"
              align="right"
              width="100"
            >
              <template #default="{ row }">
                {{ row.areaCounts[idx] || 0 }}
              </template>
            </el-table-column>
            <el-table-column
              label="合计"
              align="right"
              width="80"
            >
              <template #default="{ row }">
                <strong>{{ row.totalCount }}</strong>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>

    <!-- 二级下钻抽屉：自定义标题突出当前消防系统名（大号 + 品牌蓝） -->
    <el-drawer
      custom-class="device-stats-drill-drawer"
      :title="`${drawerTitle} - 设施类型明细`"
      :visible.sync="drawerVisible"
      direction="rtl"
      size="820px"
      append-to-body
      @open="loadTypeDistribution"
    >
      <template #title>
        <span class="drawer-title-wrap">
          <span class="drawer-title-main">{{ drawerTitle }}</span>
          <span class="drawer-title-suffix"> - 设施类型明细</span>
        </span>
      </template>
      <div
        v-loading="drawerLoading"
        class="drawer-content"
      >
        <el-table
          :data="drawerTypes"
          border
          size="small"
          show-summary
          :summary-method="getDrawerSummaries"
        >
          <el-table-column
            label="设施类型"
            prop="deviceTypeName"
            min-width="130"
          />
          <el-table-column
            v-for="(name, idx) in drawerAreaNames"
            :key="'d-' + name"
            :label="name"
            align="right"
            width="100"
          >
            <template #default="{ row }">
              {{ row.areaCounts[idx] || 0 }}
            </template>
          </el-table-column>
          <el-table-column
            label="合计"
            align="right"
            width="80"
          >
            <template #default="{ row }">
              <strong>{{ row.totalCount }}</strong>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { deviceService } from './device.service';
import { downloadFromApiBlobResponse } from '@/features/common/utils/downloadFileUtil.js';

// 图表区域颜色序列，与汇总卡颜色保持一致
const AREA_COLORS = ['#2296f7', '#67c23a', '#e6a23c', '#f56c6c', '#a27ce3'];
const AREA_COLOR_CLASSES = ['primary', 'success', 'warning', 'danger', 'purple'];

export default {
  name: 'DeviceStatisticsDashboard',
  data () {
    return {
      loading: false,
      matrixExportLoading: false,
      areaNames: [],
      systems: [],
      chart: null,
      drawerVisible: false,
      drawerLoading: false,
      drawerTitle: '',
      drawerSystemId: '',
      drawerTypes: [],
      // 下钻接口按当前系统实际出现的区域返回 areaNames，与 types[].areaCounts 下标一致，不可沿用主页 areaNames
      drawerAreaNames: []
    };
  },
  computed: {
    /** 每个一级区域的设备总量（跨所有系统求和） */
    areaTotals () {
      if (!this.areaNames.length || !this.systems.length) return [];
      return this.areaNames.map((_, idx) =>
        this.systems.reduce((sum, s) => sum + (s.areaCounts[idx] || 0), 0)
      );
    },
    /** 设备总数 */
    grandTotal () {
      return this.systems.reduce((sum, s) => sum + (s.totalCount || 0), 0);
    },
    /** 是否有设备数据（用于空态与导出禁用） */
    hasDeviceData () {
      return this.grandTotal > 0;
    }
  },
  methods: {
    /** 根据区域索引返回颜色类名 */
    areaColorClass (idx) {
      return AREA_COLOR_CLASSES[idx % AREA_COLOR_CLASSES.length];
    },

    async handleExportMatrix () {
      this.matrixExportLoading = true;
      try {
        const res = await deviceService.exportSystemDistributionMatrix();
        downloadFromApiBlobResponse(res, '设备数量统计矩阵.xlsx');
        this.$message.success('导出成功');
      } catch (e) {
        this.$message.error('导出失败');
      } finally {
        this.matrixExportLoading = false;
      }
    },

    /** 加载统计数据 */
    async loadData () {
      this.loading = true;
      try {
        const res = await deviceService.getSystemDistribution();
        const data = res.data;
        this.areaNames = data.areaNames || [];
        this.systems = data.systems || [];
        this.$nextTick(() => this.renderChart());
      } catch (e) {
        this.$message.error('获取统计数据失败');
      } finally {
        this.loading = false;
      }
    },

    /** 渲染图表 */
    renderChart () {
      const container = this.$refs.chartContainer;
      if (!container) return;

      if (!this.systems.length || !this.grandTotal) {
        if (this.chart) this.chart.clear();
        return;
      }

      if (!this.chart) {
        this.chart = echarts.init(container);
        // 图表点击下钻：params.dataIndex 与下方 sorted / yAxis.data 同序，直接用下标取系统
        this.chart.on('click', (params) => {
          if (params.componentType !== 'series' || params.dataIndex == null) return;
          const sortedList = [...this.systems].sort((a, b) => a.totalCount - b.totalCount);
          const system = sortedList[params.dataIndex];
          if (system) this.openDrillDown(system);
        });
      }

      // Y轴按总数升序（ECharts 横向柱图 Y 轴是从下往上，视觉上降序排列需要升序数据）
      const sorted = [...this.systems].sort((a, b) => a.totalCount - b.totalCount);
      const yAxisData = sorted.map(s => s.systemName);

      const series = this.areaNames.map((name, idx) => ({
        name,
        type: 'bar',
        stack: 'total',
        label: {
          show: false
        },
        itemStyle: { color: AREA_COLORS[idx % AREA_COLORS.length] },
        data: sorted.map(s => s.areaCounts[idx] || 0)
      }));

      this.chart.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const systemName = params[0].axisValue;
            let html = `<strong>${systemName}</strong><br/>`;
            params.forEach(p => {
              html += `${p.marker}${p.seriesName}：${p.value}<br/>`;
            });
            const total = params.reduce((s, p) => s + p.value, 0);
            html += `<strong>合计：${total}</strong>`;
            return html;
          }
        },
        legend: {
          data: this.areaNames,
          top: 0,
          right: 10
        },
        grid: {
          left: 20,
          right: 20,
          top: 36,
          bottom: 10,
          containLabel: true
        },
        xAxis: {
          type: 'value',
          minInterval: 1
        },
        yAxis: {
          type: 'category',
          data: yAxisData,
          axisLabel: {
            width: 120,
            overflow: 'truncate'
          }
        },
        series
      });
    },

    /** 打开下钻抽屉 */
    openDrillDown (system) {
      this.drawerSystemId = system.systemId;
      this.drawerTitle = system.systemName;
      this.drawerTypes = [];
      this.drawerAreaNames = [];
      this.drawerVisible = true;
    },

    /** 加载设施类型分布数据 */
    async loadTypeDistribution () {
      if (!this.drawerSystemId) return;
      this.drawerLoading = true;
      try {
        const res = await deviceService.getTypeDistribution(this.drawerSystemId);
        const data = res.data || {};
        this.drawerAreaNames = data.areaNames && data.areaNames.length ? data.areaNames : this.areaNames;
        this.drawerTypes = data.types || [];
      } catch (e) {
        this.$message.error('获取设施类型数据失败');
      } finally {
        this.drawerLoading = false;
      }
    },

    /** 矩阵表合计行 */
    getSummaries ({ columns, data }) {
      return columns.map((col, idx) => {
        if (idx === 0) return '合计';
        if (col.property === undefined && idx === columns.length - 1) {
          // 最后一列：总合计
          return data.reduce((s, row) => s + (row.totalCount || 0), 0);
        }
        // 区域列（按列索引减1得 areaCounts 下标）
        const areaIdx = idx - 1;
        if (areaIdx >= 0 && areaIdx < this.areaNames.length) {
          return data.reduce((s, row) => s + (row.areaCounts[areaIdx] || 0), 0);
        }
        return '';
      });
    },

    /** 抽屉表合计行：区域列数与 drawerAreaNames 一致 */
    getDrawerSummaries ({ columns, data }) {
      const names = this.drawerAreaNames || [];
      return columns.map((col, idx) => {
        if (idx === 0) return '合计';
        if (col.property === undefined && idx === columns.length - 1) {
          return data.reduce((s, row) => s + (row.totalCount || 0), 0);
        }
        const areaIdx = idx - 1;
        if (areaIdx >= 0 && areaIdx < names.length) {
          return data.reduce((s, row) => s + (row.areaCounts[areaIdx] || 0), 0);
        }
        return '';
      });
    }
  },
  mounted () {
    this.loadData();
    this._resizeObserver = new ResizeObserver(() => {
      if (this.chart) this.chart.resize();
    });
    this._resizeObserver.observe(this.$el);
  },
  beforeDestroy () {
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  }
};
</script>

<style scoped lang="scss">
.stats-dashboard-page {
  padding: 20px;
  background: #f5f7fa;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 15px;
  overflow: auto;

  .page-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;

    .page-title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }
  }

  .stats-row {
    flex-shrink: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 15px;

    .stat-item {
      background: #fff;
      border-radius: 8px;
      padding: 18px;
      text-align: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

      .stat-item-value {
        font-size: 28px;
        font-weight: 600;
        margin-bottom: 5px;
      }

      .stat-item-label {
        font-size: 14px;
        color: #909399;
      }

      &.primary .stat-item-value { color: #2296f7; }
      &.success .stat-item-value { color: #67c23a; }
      &.warning .stat-item-value { color: #e6a23c; }
      &.danger  .stat-item-value { color: #f56c6c; }
      &.purple  .stat-item-value { color: #a27ce3; }
    }
  }

  .content-row {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
    align-items: stretch;

    // 网格子项默认 min-height:auto 会撑开行高且无法在内侧滚动，限制高度以便右侧表格区域可滚动
    > .card {
      min-height: 0;
    }

    @media (max-width: 1100px) {
      grid-template-columns: 1fr;
    }
  }

  .card {
    background: #fff;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .card-header {
      flex-shrink: 0;
      display: flex;
      align-items: baseline;
      gap: 10px;
      margin-bottom: 15px;

      &.card-header-with-action {
        align-items: center;
        justify-content: space-between;
      }

      .card-title {
        font-size: 15px;
        font-weight: 600;
        color: #303133;
      }

      .card-subtitle {
        font-size: 12px;
        color: #909399;
      }
    }
  }

  .chart-card {
    position: relative;

    .chart-container {
      flex: 1;
      min-height: 300px;
    }
  }

  .panel-empty {
    flex: 1;
    min-height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: #909399;
    background: #fafafa;
    border-radius: 4px;
  }

  .matrix-empty {
    min-height: 200px;
  }

  .table-card {
    overflow: hidden;

    .matrix-table-scroll {
      flex: 1;
      min-height: 0;
      overflow: auto;
    }

    .matrix-table {
      width: 100%;

      // 避免合计行与表体列宽错位（fixed 列 + summary 时尤其明显；统一 footer 表宽）
      ::v-deep .el-table__footer-wrapper table {
        width: 100% !important;
      }
      ::v-deep .el-table__footer-wrapper td {
        vertical-align: middle;
      }
    }
  }
}

.drawer-content {
  padding: 16px;
}
</style>

<!-- append-to-body 时抽屉 DOM 不在当前组件根下，需单独样式块 -->
<style lang="scss">
.device-stats-drill-drawer {
  .el-drawer__header {
    margin-bottom: 0;
    padding-bottom: 20px;
    border-bottom: 1px solid #ebeef5;
  }

  .drawer-title-wrap {
    display: inline-block;
    line-height: 1.4;
    vertical-align: middle;
  }

  .drawer-title-main {
    font-size: 20px;
    font-weight: 600;
    color: #409eff;
  }

  .drawer-title-suffix {
    font-size: 14px;
    font-weight: 400;
    color: #606266;
  }
}
</style>
